import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, X, ArrowRight, Check, Sparkles } from "lucide-react";
import { useJourney } from "../context/JourneyContext";
import { JOURNEY } from "../lib/journey";

// Rutas que no forman parte del recorrido por etapas.
const HIDDEN_ROUTES = ["/programs"];

export const JourneyFab = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    currentStage,
    nextStage,
    whyNext,
    reached,
    completed,
    returning,
    completeStage,
    openAssessment,
  } = useJourney();

  const [open, setOpen] = useState(false);

  const filled = new Set([...(reached || []), ...(completed || [])]);
  const currentN = Number(currentStage?.n || 1);
  const isCurrentDone = completed.includes(String(currentN));

  if (HIDDEN_ROUTES.includes(pathname)) return null;

  const goNext = () => {
    setOpen(false);
    navigate(nextStage ? nextStage.to : "/events");
  };

  return (
    <div className="fixed bottom-5 right-5 z-40 sm:bottom-7 sm:right-7" data-testid="journey-fab">
      <AnimatePresence mode="wait">
        {open ? (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[85vh] w-[min(21rem,calc(100vw-2.5rem))] overflow-y-auto rounded-sm border border-gold/25 bg-navy/95 shadow-2xl shadow-black/40 backdrop-blur-xl"
          >
            <div className="sticky top-0 flex items-center justify-between border-b border-gold/15 bg-navy/95 px-6 py-4 backdrop-blur-xl">
              <div>
                <p className="font-serif text-xl leading-none text-cream">Tu Viaje</p>
                <p className="mt-1 text-[0.6rem] uppercase tracking-luxe text-gold/70">
                  Una transformación en seis etapas
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-cream/60 transition-colors hover:text-gold"
                data-testid="journey-fab-close"
                aria-label="Cerrar"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-6 p-6">
              {returning && (
                <div className="rounded-sm border border-gold/20 bg-gold/10 p-4" data-testid="journey-fab-welcome">
                  <p className="font-serif text-xl leading-tight text-cream">Bienvenido de nuevo.</p>
                  <p className="mt-1.5 text-sm font-light leading-relaxed text-cream/75">
                    Continúa tu viaje de transformación donde lo dejaste.
                  </p>
                </div>
              )}

              {/* current stage */}
              <div data-testid="journey-fab-current">
                <p className="overline mb-3">Etapa actual</p>
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold bg-gold text-navy">
                    <currentStage.icon size={18} />
                  </span>
                  <div>
                    <span className="block text-[0.55rem] uppercase tracking-luxe text-gold/60">
                      Nivel 0{currentStage.n}
                    </span>
                    <span className="block font-serif text-2xl leading-tight text-cream">{currentStage.title}</span>
                  </div>
                </div>
                {!isCurrentDone && (
                  <button
                    onClick={() => completeStage(currentN)}
                    className="mt-3 flex items-center gap-2 text-xs font-light text-cream/60 transition-colors hover:text-gold"
                    data-testid="journey-fab-complete"
                  >
                    <Check size={13} /> Marcar esta etapa como completada
                  </button>
                )}
                {isCurrentDone && (
                  <p className="mt-3 flex items-center gap-2 text-xs font-light text-gold">
                    <Check size={13} /> Etapa completada · siguiente desbloqueada
                  </p>
                )}
              </div>

              {/* next recommended step + why */}
              <div data-testid="journey-fab-next">
                <p className="overline mb-3">Siguiente paso recomendado</p>
                <button
                  onClick={goNext}
                  className="group flex w-full items-center gap-3 rounded-sm border border-gold bg-gold/5 p-4 text-left transition-colors hover:bg-gold/10"
                  data-testid="journey-fab-cta"
                >
                  {nextStage ? (
                    <>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold">
                        <nextStage.icon size={16} />
                      </span>
                      <span className="flex-1">
                        <span className="block text-[0.55rem] uppercase tracking-luxe text-gold/70">
                          Nivel 0{nextStage.n}
                        </span>
                        <span className="block font-serif text-xl leading-tight text-cream">{nextStage.title}</span>
                      </span>
                    </>
                  ) : (
                    <span className="flex-1">
                      <span className="block font-serif text-xl leading-tight text-cream">Sigue integrando</span>
                    </span>
                  )}
                  <ArrowRight size={16} className="shrink-0 text-gold transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <div className="mt-3">
                  <p className="text-[0.6rem] uppercase tracking-luxe text-gold/60">Por qué es tu siguiente paso</p>
                  <p className="mt-1 text-xs font-light leading-relaxed text-cream/70">{whyNext}</p>
                </div>
              </div>

              {/* transformation progress (words over numbers) */}
              <div data-testid="journey-fab-progress">
                <p className="overline mb-3">Progreso de transformación</p>
                <div className="flex gap-1.5">
                  {JOURNEY.map((s) => (
                    <span
                      key={s.n}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        filled.has(s.n) ? "bg-gold" : "bg-cream/20"
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-3 text-xs font-light leading-relaxed text-cream/60">
                  Has comenzado {filled.size || 1} de 6 etapas. Sin prisa — este es un camino que se vive a tu ritmo.
                </p>
              </div>

              <button
                onClick={() => {
                  setOpen(false);
                  openAssessment();
                }}
                className="flex items-center justify-center gap-2 rounded-full border border-gold/40 py-3 text-xs font-medium tracking-wide text-gold transition-colors hover:bg-gold hover:text-navy"
                data-testid="journey-fab-assessment"
              >
                <Sparkles size={14} /> ¿No sabes por dónde empezar?
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.button
            key="fab"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            whileHover={{ y: -3 }}
            onClick={() => setOpen(true)}
            className="flex items-center gap-0 rounded-full border border-gold/30 bg-navy/90 p-2.5 shadow-xl shadow-black/30 backdrop-blur-xl md:gap-3 md:py-3 md:pl-4 md:pr-5"
            data-testid="journey-fab-toggle"
          >
            <span className="relative flex h-8 w-8 items-center justify-center rounded-full border border-gold/40 text-gold">
              <Compass size={16} />
              {returning && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-gold animate-pulse-ring" />
              )}
            </span>
            <span className="hidden text-left md:block">
              <span className="block text-[0.55rem] uppercase tracking-luxe text-gold/70">
                Tu Viaje · Nivel 0{currentN}
              </span>
              <span className="flex items-center gap-1.5 pt-1">
                {JOURNEY.map((s) => (
                  <span
                    key={s.n}
                    className={`h-1.5 w-1.5 rounded-full ${filled.has(s.n) ? "bg-gold" : "bg-cream/25"}`}
                  />
                ))}
              </span>
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};
